import { gsap } from 'gsap';

/**
 * ButtonHover - GSAP-powered button hover animations
 * 
 * Implements staggered hover effect:
 * 1. Text color fades quickly (~100ms)
 * 2. Background wipes from left with aggressive ease (power4.out)
 * 
 * Applies to .button elements, excluding navigation
 */
class ButtonHover {
  constructor() {
    this.buttons = [];
    this.init();
  }

  init() {
    // Select all buttons, excluding navigation-specific elements
    const allButtons = document.querySelectorAll('.button');
    
    console.log(`🎯 ButtonHover: Found ${allButtons.length} button elements`);
    
    allButtons.forEach(button => {
      // Skip if button is inside navigation or is a nav-specific element
      if (
        button.closest('nav') || 
        button.closest('.header') ||
        button.closest('.nav-overlay') ||
        button.classList.contains('nav-overlay-cta')
      ) {
        console.log(`  ⏭️  Skipping nav button:`, button.className);
        return;
      }

      console.log(`  ✅ Adding hover to:`, button.className);
      this.buttons.push(button);
      this.setupButton(button);
    });
    
    console.log(`🎯 ButtonHover: Initialized ${this.buttons.length} buttons`);
  }

  setupButton(button) {
    // Get the ::before pseudo-element will be controlled via CSS custom properties
    // Store initial state
    const isLightButton = button.classList.contains('btn-light');
    const textColorHover = isLightButton ? '#343B30' : '#FFF6DF'; // pine or buttermilk
    const textColorDefault = isLightButton ? '#C7CCB6' : '#7D3904'; // lichen or burnt-orange
    const isSubmitInput = button.matches('input[type="submit"]');
    const backgroundColor = isLightButton ? '#C7CCB6' : '#7D3904';

    let hoverTimeline = null;

    if (isSubmitInput) {
      // Submit inputs cannot render ::before, so mirror the wipe with a gradient.
      gsap.set(button, {
        backgroundImage: `linear-gradient(90deg, ${backgroundColor} 0%, ${backgroundColor} 50%, transparent 50%, transparent 100%)`,
        backgroundSize: '200% 100%',
        backgroundPosition: '100% 0%'
      });
    } else {
      // Set initial ::before state via inline style
      gsap.set(button, {
        '--bg-x': '-100%'
      });
    }

    // Mouseenter: staggered animation
    button.addEventListener('mouseenter', () => {
      if (hoverTimeline) {
        hoverTimeline.kill();
      }

      const tl = gsap.timeline();
      hoverTimeline = tl;
      
      // Step 1: Background wipe (aggressive ease)
      if (isSubmitInput) {
        tl.to(button, {
          backgroundPosition: '0% 0%',
          duration: 0.4,
          ease: 'power4.out'
        }, 0);
      } else {
        tl.to(button, {
          '--bg-x': '0%',
          duration: 0.4,
          ease: 'power4.out'
        }, 0);
      }

      // Step 2: Text color change (starts once wipe begins)
      tl.to(button, {
        color: textColorHover,
        duration: 0.15,
        ease: 'power2.out'
      }, 0.12);
    });

    // Mouseleave: reverse animation
    button.addEventListener('mouseleave', () => {
      if (hoverTimeline) {
        hoverTimeline.kill();
      }

      const tl = gsap.timeline();
      hoverTimeline = tl;
      
      // Background wipes out first
      if (isSubmitInput) {
        tl.to(button, {
          backgroundPosition: '100% 0%',
          duration: 0.3,
          ease: 'power2.in'
        }, 0);
      } else {
        tl.to(button, {
          '--bg-x': '-100%',
          duration: 0.3,
          ease: 'power2.in'
        }, 0);
      }
      
      // Text color returns
      tl.to(button, {
        color: textColorDefault,
        duration: 0.15,
        ease: 'power2.in'
      }, 0.15);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ButtonHover();
  });
} else {
  new ButtonHover();
}

export default ButtonHover;
